import React from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import TopBanner from '../components/TopBanner';

export default function Teams() {
  const homeimg = '/images/home/home.jpg';
  const server_url = process.env.REACT_APP_SERVER;

  // Animation Variants
  const imageVariants = (direction) => ({
    hidden: { opacity: 0, x: direction === 'left' ? -60 : 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
  });

  const textVariants = (direction) => ({
    hidden: { opacity: 0, x: direction === 'left' ? -40 : 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.2, ease: "easeOut" } }
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LuranTech",
    "url": `${server_url}`,
    "logo": `${server_url}/images/logo.png`,
    "department": {
      "@type": "Organization",
      "name": "LuranTech Team",
      "url": "https://www.lurantech.com/teams",
      "employee": [
        {
          "@type": "Person",
          "name": "Dipti Ranparia",
          "jobTitle": "Director & Chief Technology Officer",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "CollegeOrUniversity", "name": "Sardar Patel University" },
          "description": "Visionary CTO leading LuranTech's technical strategy and innovation with 18+ years of industry experience."
        },
        {
          "@type": "Person",
          "name": "Gunjan Ranparia",
          "jobTitle": "Co-Founder",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "EducationalOrganization", "name": "BE in Computer Engineering" },
          "description": "Co-Founder driving tech-business alignment and strategic innovation at LuranTech."
        },
        {
          "@type": "Person",
          "name": "Dhaval Vaghasiya",
          "jobTitle": "Business & Sales Manager",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "EducationalOrganization", "name": "BE in Mechanical Engineering" },
          "description": "Sales leader building client relationships and growth strategies for LuranTech."
        },
        {
          "@type": "Person",
          "name": "Trupti Thummar",
          "jobTitle": "QA Developer",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "EducationalOrganization", "name": "Master of Computer Applications" },
          "description": "Quality Assurance expert ensuring reliability and performance of LuranTech products."
        },
        {
          "@type": "Person",
          "name": "Parth Radadiya",
          "jobTitle": "Senior Developer",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "EducationalOrganization", "name": "BE in Electronics" },
          "description": "Senior Developer building scalable and high-performance software solutions."
        },
        {
          "@type": "Person",
          "name": "Yamish Bhalara",
          "jobTitle": "Senior Developer",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "EducationalOrganization", "name": "BE in Mechanical Engineering" },
          "description": "Full-stack developer creating robust, scalable software at LuranTech."
        },
        {
          "@type": "Person",
          "name": "Sudhir Vaghasiya",
          "jobTitle": "Software Developer",
          "worksFor": { "@type": "Organization", "name": "LuranTech" },
          "alumniOf": { "@type": "EducationalOrganization", "name": "Bachelor of Commerce" },
          "description": "Developer turning ideas into functional, maintainable code at LuranTech."
        }
      ]
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <meta property="og:title" content="Teams - Meet the Minds Driving Our Success | Lurantech" />
          <meta property="og:description" content="Discover the talented professionals behind Lurantech. Our dedicated IT teams drive innovation and excellence in technology solutions. Learn more about our experts." />
          <meta property="og:url" content={`${server_url}/home`} />
          <meta property="og:image" content={`${server_url}${homeimg}`} />
          <meta property="og:type" content="website" />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="675" />
          
          <meta name="twitter:title" content="Teams - Meet the Minds Driving Our Success | Lurantech" />
          <meta name="twitter:description" content="Discover the talented professionals behind Lurantech. Our dedicated IT teams drive innovation and excellence in technology solutions. Learn more about our experts." />
          <meta name="twitter:image" content={`${server_url}${homeimg}`}  />
          <meta name="twitter:card" content="summary_large_image" />
          <meta property="twitter:image:type" content="image/jpeg" />
          <meta property="twitter:image:width" content="1200" />
          <meta property="twitter:image:height" content="675" />
          <meta name="geo.position" content="22.3026;73.1812" />
          <meta name="geo.placename" content="4th Floor - Office 1, 7, Sky Complex, Laxmipura, Vadodara, Gujarat 390021" />
          <meta name="geo.region" content="IN-GJ" />
          <meta property="og:latitude" content="22.3026" />
          <meta property="og:longitude" content="73.1812" />
          <meta property="og:location" content="4th Floor - Office 1, 7, Sky Complex, Laxmipura, Vadodara, Gujarat 390021" />

          <meta name="robots" content="index, follow" />
          <meta name="description" content="Discover the talented professionals behind Lurantech. Our dedicated IT teams drive innovation and excellence in technology solutions. Learn more about our experts." />
          <meta name="keywords" content="Lurantech team, IT professionals, Technology experts, Software development team, Meet our team, Lurantech careers, IT consulting team, liferay consulting, cms solutions, software development, Liferay development, cloud managment, digital transformation, e-commorce solutions, enterprise solutions" />
          <meta name="author" content="Lurantech" />

          <link rel="canonical" href={`${server_url}/home`} />
          <link rel="preload" href={`${server_url}${homeimg}`} as="image" />
          <title>Teams - Meet the Minds Driving Our Success | Lurantech</title>
        </Helmet>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaData),
          }}
        />

        <TopBanner title="Meet the Minds Driving Our Success" bgimage='/images/teams/people-bg.jpg'/>
        
        <div className="how-section1 container">
          {/* Row 1: Dipti (Image Left) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
              <img src="/images/teams/ceo.jpg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Dipti Ranparia"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('right')}
            >
              <h4>Dipti Ranparia</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>Director & Chief Technology Officer</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Leading the Charge in Innovation</q></h4>
              <p className="text-muted">As Director & CTO at LuranTech, Dipti is the visionary architect behind the company's technical strategy and innovation roadmap. With over 18 years of experience in the technology industry, she brings deep expertise in leading high-performing teams and delivering innovative, business-driven solutions.
                <br />Dipti holds a Bachelor degree in Computer Science from Sardar Patel University. Her extensive experience and strong technology leadership ensure that LuranTech remains at the forefront of industry advancements.
                <br />In addition to her work at LuranTech, Dipti actively volunteers with <a target="_blank" className="luran-link" rel="noreferrer" href="https://www.womeninai.co/">Women in AI</a>, helping to support initiatives that empower women in the field of artificial intelligence.
                <br />Outside of her professional life, she enjoys spending quality time with her family, cooking, and engaging in farming.</p>
              <div className='d-flex align-items-center'>Connect on :
                <a href="https://www.linkedin.com/in/diptiranparia" target="_blank" rel="noreferrer" className="linkedin-link text-sm font-weight-bold d-flex align-items-center ms-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-linkedin me-2" viewBox="0 0 16 16">
                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708C16 15.487 15.474 16 14.825 16H1.175C.526 16 0 15.487 0 14.854V1.146zM4.943 13.569V6.169H2.542v7.4h2.401zm-1.2-8.412c.837 0 1.355-.554 1.355-1.248-.015-.71-.518-1.249-1.34-1.249-.823 0-1.356.54-1.356 1.249 0 .694.518 1.248 1.324 1.248h.017zm4.908 8.412V9.359c0-.224.016-.447.082-.606.18-.447.588-.911 1.274-.911.899 0 1.258.687 1.258 1.694v4.033h2.4V9.3c0-2.291-1.223-3.356-2.855-3.356-1.312 0-1.9.726-2.228 1.234h.016V6.169H7.651c.03.674 0 7.4 0 7.4h2.401z"/>
                  </svg>
                  Dipti Ranparia
                </a>
              </div>
            </motion.div>
          </div>

          {/* Row 2: Gunjan (Image Right) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img d-block d-md-none"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
              <img src="/images/teams/gunjan.jpeg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Gunjan Ranparia"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('left')}
            >
              <h4>Gunjan Ranparia</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>Co-Founder</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Crafting Tomorrow's Tech, Today</q></h4>
              <p className="text-muted">Gunjan is one of the key pillars of LuranTech, bringing a wealth of entrepreneurial spirit and a relentless drive to build impactful solutions. As the Co-Founder, she focuses on bridging technology and business strategy, ensuring LuranTech continuously delivers value-driven services that meet the evolving needs of clients and industries alike.
                <br />Gunjan holds a Bachelor of Engineering in Computer Engineering - BE CE, which underpins her strong technical foundation and strategic thinking.</p>
              <div className='d-flex align-items-center'>Connect on :
                <a href="https://www.linkedin.com/in/gunjan-ranparia-6a8009114" target="_blank" rel="noreferrer" className="linkedin-link text-sm font-weight-bold d-flex align-items-center ms-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-linkedin me-2" viewBox="0 0 16 16">
                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708C16 15.487 15.474 16 14.825 16H1.175C.526 16 0 15.487 0 14.854V1.146zM4.943 13.569V6.169H2.542v7.4h2.401zm-1.2-8.412c.837 0 1.355-.554 1.355-1.248-.015-.71-.518-1.249-1.34-1.249-.823 0-1.356.54-1.356 1.249 0 .694.518 1.248 1.324 1.248h.017zm4.908 8.412V9.359c0-.224.016-.447.082-.606.18-.447.588-.911 1.274-.911.899 0 1.258.687 1.258 1.694v4.033h2.4V9.3c0-2.291-1.223-3.356-2.855-3.356-1.312 0-1.9.726-2.228 1.234h.016V6.169H7.651c.03.674 0 7.4 0 7.4h2.401z"/>
                  </svg>
                  Gunjan Ranparia
                </a>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 how-img d-none d-md-block"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('right')}
            >
              <img src="/images/teams/gunjan.jpeg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Gunjan Ranparia"/>
            </motion.div>
          </div>

          {/* Row 3: Dhaval (Image Left) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
               <img src="/images/teams/dhaval.jpg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Dhaval Vaghasiya"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('right')}
            >
              <h4>Dhaval Vaghasiya</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>Business & Sales Manager</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Turning Connections Into Opportunities</q></h4>
              <p className="text-muted">Dhaval is the mastermind behind LuranTech's business development and sales strategies. With a keen eye for spotting opportunities and building lasting relationships, he ensures the company's growth by aligning its services with clients' unique needs. His ability to understand market dynamics and customer aspirations makes him the perfect catalyst for expanding LuranTech's footprint.
                <br />He holds a Bachelor of Engineering in Mechanical Engineering - BE Mech, combining technical understanding with business acumen.</p>
            </motion.div>
          </div>

          {/* Row 4: Trupti (Image Right) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img d-block d-md-none"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
              <img src="/images/teams/trupti.jpeg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Trupti Thummar"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('left')}
            >
              <h4>Trupti Thummar</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>QA Developer</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Ensuring Quality at Every Step</q></h4>
              <p className="text-muted">Trupti's meticulous eye for detail and expertise in quality assurance guarantees that every product delivered by LuranTech meets the highest standards of excellence. As a QA Developer, she is dedicated to identifying, fixing, and preventing issues, ensuring that each piece of software is reliable, secure, and ready to perform in real-world conditions.
                <br />She holds a Master of Computer Applications - MCA, equipping her with in-depth knowledge of software development and testing methodologies.</p>
            </motion.div>
            <motion.div 
              className="col-md-4 how-img d-none d-md-block"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('right')}
            >
              <img src="/images/teams/trupti.jpeg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Trupti Thummar"/>
            </motion.div>
          </div>

          {/* Row 5: Parth (Image Left) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
               <img src="/images/teams/parth.png" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Parth Radadiya"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('right')}
            >
              <h4>Parth Radadiya</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>Senior Developer</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Building the Future, One Line of Code at a Time</q></h4>
              <p className="text-muted">Parth is a Senior Developer at LuranTech, known for his exceptional problem-solving abilities and deep technical knowledge. His work is central to developing high-performance applications that drive the company's solutions forward.
                <br />Parth holds a Bachelor of Engineering in Electrical, which strengthens his grasp of complex systems and scalable software design.</p>
              <div className='d-flex align-items-center'>Connect on :
                <a href="https://www.linkedin.com/in/parth-radadiya-7b1bb037/" target="_blank" rel="noreferrer" className="linkedin-link text-sm font-weight-bold d-flex align-items-center ms-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-linkedin me-2" viewBox="0 0 16 16">
                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708C16 15.487 15.474 16 14.825 16H1.175C.526 16 0 15.487 0 14.854V1.146zM4.943 13.569V6.169H2.542v7.4h2.401zm-1.2-8.412c.837 0 1.355-.554 1.355-1.248-.015-.71-.518-1.249-1.34-1.249-.823 0-1.356.54-1.356 1.249 0 .694.518 1.248 1.324 1.248h.017zm4.908 8.412V9.359c0-.224.016-.447.082-.606.18-.447.588-.911 1.274-.911.899 0 1.258.687 1.258 1.694v4.033h2.4V9.3c0-2.291-1.223-3.356-2.855-3.356-1.312 0-1.9.726-2.228 1.234h.016V6.169H7.651c.03.674 0 7.4 0 7.4h2.401z"/>
                  </svg>
                  Parth Radadiya
                </a>
              </div>
            </motion.div>
          </div>

          {/* Row 6: Yamish (Image Right) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img d-block d-md-none"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
              <img src="/images/teams/yamish.jpg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Yamish Bhalara"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('left')}
            >
              <h4>Yamish Bhalara</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>Senior Developer</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Crafting Seamless Solutions with Precision</q></h4>
              <p className="text-muted">With a focus on building scalable and efficient solutions, Yamish brings technical brilliance to the team as a Senior Developer. His expertise in full-stack development enables LuranTech to create applications that are both powerful and user-friendly.
                <br />He holds a Bachelor of Engineering in Mechanical Engineering - BE Mech, bringing a problem-solving mindset and well-rounded technical expertise to every project.</p>
              <div className='d-flex align-items-center'>Connect on :
                <a href="https://www.linkedin.com/in/yamish-bhalara" target="_blank" rel="noreferrer" className="linkedin-link text-sm font-weight-bold d-flex align-items-center ms-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-linkedin me-2" viewBox="0 0 16 16">
                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708C16 15.487 15.474 16 14.825 16H1.175C.526 16 0 15.487 0 14.854V1.146zM4.943 13.569V6.169H2.542v7.4h2.401zm-1.2-8.412c.837 0 1.355-.554 1.355-1.248-.015-.71-.518-1.249-1.34-1.249-.823 0-1.356.54-1.356 1.249 0 .694.518 1.248 1.324 1.248h.017zm4.908 8.412V9.359c0-.224.016-.447.082-.606.18-.447.588-.911 1.274-.911.899 0 1.258.687 1.258 1.694v4.033h2.4V9.3c0-2.291-1.223-3.356-2.855-3.356-1.312 0-1.9.726-2.228 1.234h.016V6.169H7.651c.03.674 0 7.4 0 7.4h2.401z"/>
                  </svg>
                  Yamish Bhalara
                </a>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 how-img d-none d-md-block"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('right')}
            >
              <img src="/images/teams/yamish.jpg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Yamish Bhalara"/>
            </motion.div>
          </div>

          {/* Row 7: Sudhir (Image Left) */}
          <div className="row mb-5 overflow-hidden">
            <motion.div 
              className="col-md-4 how-img"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={imageVariants('left')}
            >
               <img src="/images/teams/sudhir.jpeg" className="rounded-circle img-fluid border-white border-w-5 shadow-sm" alt="Sudhir Vaghasiya"/>
            </motion.div>
            <motion.div 
              className="col-md-8"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={textVariants('right')}
            >
              <h4>Sudhir Vaghasiya</h4>
              <h4 className="subheading" style={{'color':'#3E3185'}}><em>Software Developer</em></h4>
              <hr className="hr-primary w-70 ml-0 mb-3" />
              <h4 className="subheading"><q>Transforming Ideas into Functional Code</q></h4>
              <p className="text-muted">Sudhir is a Software Developer whose passion for coding drives him to craft innovative and effective solutions. With a focus on clean and efficient code, he helps turn abstract ideas into fully functional applications. Sudhir's attention to detail and dedication to quality make him an essential part of the development process at LuranTech.
                <br />He holds a Bachelor of Commerce, which provides him with a unique blend of analytical skills and technical curiosity.</p>
            </motion.div>
          </div>
        </div>
      </HelmetProvider>
    </>
  );
}